// Sentry browser (client) SDK initialization — shared across all corekit apps.
//
// How it works:
//   Next.js automatically imports instrumentation-client.ts on every page load.
//   That file imports this module, which calls Sentry.init() as a side effect.
//   Guard: if NEXT_PUBLIC_SENTRY_DSN is not set, nothing is initialised and
//   there is zero runtime overhead.
import * as Sentry from '@sentry/nextjs';

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,

    // Silence Sentry during local dev (next dev): no events are sent.
    // Production (NODE_ENV=production) is unaffected.
    enabled: process.env.NODE_ENV !== 'development',

    // -------------------------------------------------------------------------
    // Environment label
    // Shown in every Sentry event so you can filter by where it came from.
    // Set NEXT_PUBLIC_SENTRY_ENVIRONMENT in .env to override:
    //   local      → your own laptop
    //   production → live server (set by CI/deploy pipeline)
    //   staging    → staging server (when it exists)
    // Falls back to NODE_ENV ("development" / "production") if not set.
    // -------------------------------------------------------------------------
    environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,

    // -------------------------------------------------------------------------
    // Performance tracing
    // 0.1 = capture a full request waterfall for 10 % of page views in prod.
    // 1.0 in dev means every request is traced locally — useful for debugging.
    // Raise the prod value if you need more data; lower it to save quota.
    // -------------------------------------------------------------------------
    // browserTracingIntegration tracks browser-side performance:
    // - resource spans: how long each JS/CSS/image file took to load
    // - navigation spans: client-side route changes (complements onRouterTransitionStart)
    // Required for the "Frontend Assets" dashboard in Sentry to show data.
    integrations: [Sentry.browserTracingIntegration()],

    tracesSampleRate: process.env.NODE_ENV === 'development' ? 1.0 : 0.1,

    // -------------------------------------------------------------------------
    // Session Replay — intentionally OFF
    // The free Sentry tier has a very small replay quota (50/month).
    // To enable: add replayIntegration() to the integrations array and set
    //   replaysSessionSampleRate / replaysOnErrorSampleRate.
    // -------------------------------------------------------------------------

    // -------------------------------------------------------------------------
    // Privacy: do NOT attach personally identifiable data (IP address, cookies,
    // request headers with auth tokens, logged-in user info) to events.
    // Required for GDPR compliance — do not set this to true without legal sign-off.
    // -------------------------------------------------------------------------
    sendDefaultPii: false,
  });
}

// Tells Sentry to start a new trace span whenever the App Router navigates
// client-side (link clicks, router.push). Without this, only hard page loads
// would appear as transactions in the Performance tab.
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
